// ---------------------------------------------------------------------------
// How a shopping list is read: by aisle, by meal, or by the day it is needed.
//
// The grouping used to live in the front app and returned plain objects keyed
// by strings, which forced the renderer to parse its own keys back apart. It
// belongs here: nothing in it touches the UI, and the order of the aisles is a
// business rule — it is the order the shop is walked in.
//
// The three readings answer the three questions asked in front of a list: what
// is left to pick up in this aisle, what a given meal still needs, and what has
// to be in the basket before tonight. An alphabetical reading answered none of
// them — a shop is not walked in alphabetical order — and is gone.
// ---------------------------------------------------------------------------
import { INGREDIENT_CATEGORIES } from '../constants/categories.js';
import { DEFAULT_INGREDIENT_UNIT } from '../constants/units.js';
import { formatIngredientValue } from './ingredients.js';
/** How the member chose to read the list. */
export const SHOPPING_GROUPINGS = ['aisle', 'meal', 'day'];
export const DEFAULT_SHOPPING_GROUPING = 'aisle';
/** The two services a day holds, in the order they are eaten. */
export const MEAL_SERVICES = ['lunch', 'dinner'];
const KNOWN_CATEGORIES = new Set(INGREDIENT_CATEGORIES);
/** An unknown or missing category is shelved with the rest, never dropped. */
function toCategory(raw) {
    return KNOWN_CATEGORIES.has(raw) ? raw : 'other';
}
/**
 * Groups by aisle, in the order the shop is walked in.
 *
 * Insertion order would have done — and did, until now — but it made the list
 * come out in whatever order the ingredients happened to be aggregated in, so
 * two regenerations of the same week put the aisles in two different orders.
 * A shopping list is read while walking: its order has to hold still.
 *
 * `order` is that walk. It defaults to `INGREDIENT_CATEGORIES`, the order of
 * a French supermarket read front to back; a member who walks their own shop
 * differently passes theirs. An aisle missing from it is appended rather than
 * dropped, so a category added later still comes out somewhere.
 */
export function groupItemsByAisle(items, order = INGREDIENT_CATEGORIES) {
    const byCategory = new Map();
    for (const item of items) {
        const category = toCategory(item.category);
        const bucket = byCategory.get(category);
        if (bucket)
            bucket.push(item);
        else
            byCategory.set(category, [item]);
    }
    const walk = [...order, ...INGREDIENT_CATEGORIES.filter((c) => !order.includes(c))];
    return walk
        .filter((category) => byCategory.has(category))
        .map((category) => ({ category, items: byCategory.get(category) }));
}
/** Chronological, and within a day the lunch before the dinner. */
function byService(a, b) {
    if (a.date !== b.date)
        return a.date < b.date ? -1 : 1;
    return MEAL_SERVICES.indexOf(a.service) - MEAL_SERVICES.indexOf(b.service);
}
/**
 * Groups by meal, in the order the week is eaten.
 *
 * A dish is not a meal: the same gratin served Tuesday and Friday is two
 * meals, and a cook reading the list before Tuesday's shop wants Tuesday's
 * lines. So the groups follow the plan, and a dish served twice appears twice
 * — the item under both, since ticking it anywhere ticks the one line it is.
 *
 * A dish the plan no longer holds still has its items on the list: it comes
 * after the planned meals, with no day, rather than being folded in with the
 * items no dish asked for at all.
 */
export function groupItemsByMeal(items, meals, recipesMeta, locale) {
    const byRecipe = new Map();
    const orphans = [];
    for (const item of items) {
        const known = item.recipe_ids.filter((id) => recipesMeta[id]);
        if (known.length === 0) {
            orphans.push(item);
            continue;
        }
        for (const id of known) {
            const bucket = byRecipe.get(id);
            if (bucket)
                bucket.push(item);
            else
                byRecipe.set(id, [item]);
        }
    }
    const groups = [];
    const served = new Set();
    for (const meal of [...meals].sort(byService)) {
        const recipeId = meal.recipeId;
        if (!recipeId)
            continue;
        const mealItems = byRecipe.get(recipeId);
        if (!mealItems)
            continue;
        served.add(recipeId);
        groups.push({
            key: `${meal.date}-${meal.service}-${recipeId}`,
            recipeId,
            title: recipesMeta[recipeId]?.title ?? null,
            imageUrl: recipesMeta[recipeId]?.image_url ?? null,
            date: meal.date,
            service: meal.service,
            portions: meal.portions,
            items: mealItems,
        });
    }
    const unplanned = [...byRecipe.keys()]
        .filter((id) => !served.has(id))
        .sort((a, b) => (recipesMeta[a]?.title ?? '').localeCompare(recipesMeta[b]?.title ?? '', locale));
    for (const recipeId of unplanned) {
        groups.push({
            key: recipeId,
            recipeId,
            title: recipesMeta[recipeId]?.title ?? null,
            imageUrl: recipesMeta[recipeId]?.image_url ?? null,
            date: null,
            service: null,
            portions: null,
            items: byRecipe.get(recipeId),
        });
    }
    if (orphans.length > 0) {
        groups.push({
            key: 'no-recipe',
            recipeId: null,
            title: null,
            imageUrl: null,
            date: null,
            service: null,
            portions: null,
            items: orphans,
        });
    }
    return groups;
}
/**
 * Groups by the day the item is first needed.
 *
 * Not the day it is eaten — the day it has to be in the kitchen, which for an
 * ingredient shared by Tuesday's and Friday's meals is Tuesday. One line, one
 * day, the earliest: a list read before the shop answers « what do I have to
 * come home with », and an item repeated under every day that wants it would
 * make that question unanswerable.
 *
 * The mockup sorts these buckets by how well the food keeps rather than by
 * date. Freshness is not a property this app holds, so the day the plan states
 * is used instead — the same reading, from the data that exists.
 */
export function groupItemsByDay(items, meals) {
    const firstDayOf = new Map();
    for (const meal of meals) {
        if (!meal.recipeId)
            continue;
        const known = firstDayOf.get(meal.recipeId);
        if (!known || meal.date < known)
            firstDayOf.set(meal.recipeId, meal.date);
    }
    const byDay = new Map();
    const undated = [];
    for (const item of items) {
        const days = item.recipe_ids
            .map((id) => firstDayOf.get(id))
            .filter((day) => !!day);
        if (days.length === 0) {
            undated.push(item);
            continue;
        }
        const day = days.reduce((earliest, candidate) => (candidate < earliest ? candidate : earliest));
        const bucket = byDay.get(day);
        if (bucket)
            bucket.push(item);
        else
            byDay.set(day, [item]);
    }
    const groups = [...byDay.keys()]
        .sort()
        .map((date) => ({ date, items: byDay.get(date) }));
    if (undated.length > 0)
        groups.push({ date: null, items: undated });
    return groups;
}
/**
 * The dishes a line was bought for, so a line read in the aisle order still
 * says what it is doing there.
 *
 * A recipe that has since been deleted leaves an id with no metadata: it is
 * left out rather than printed as an unknown dish, which would tell the member
 * nothing and push the real answer out of the line.
 */
export function shoppingItemOrigins(item, recipesMeta, locale) {
    const titles = item.recipe_ids
        .map((id) => recipesMeta[id]?.title)
        .filter((title) => !!title);
    return [...new Set(titles)].sort((a, b) => a.localeCompare(b, locale));
}
/**
 * How many portions each dish of a list was bought for, recipe id → portions.
 *
 * The list records it when it writes its quantities (`recipe_portions`): the
 * figure the quantities were computed from, whatever the plan became since.
 * A list made before that column records nothing, and is read off the plan
 * instead — a dish cooked twice over its days adds up, the way the
 * generation added up its quantities.
 */
export function shoppingRecipePortions(recorded, meals) {
    if (recorded && Object.keys(recorded).length > 0)
        return { ...recorded };
    const portions = {};
    for (const meal of meals) {
        if (!meal.recipeId || meal.portions === null)
            continue;
        portions[meal.recipeId] = (portions[meal.recipeId] ?? 0) + meal.portions;
    }
    return portions;
}
/**
 * The dishes a line was bought for, each with the portions it is cooked for —
 * what a line unfolds into when someone wonders why a kilo of potatoes is on
 * the list, and whether that is for four or for twelve.
 *
 * `portions` is `shoppingRecipePortions`. A dish it has no figure for is
 * still named, without a figure rather than with a wrong one.
 */
export function shoppingItemRecipes(item, portions, recipesMeta, locale) {
    return [...new Set(item.recipe_ids)]
        .filter((id) => recipesMeta[id])
        .map((recipeId) => ({
        recipeId,
        title: recipesMeta[recipeId]?.title ?? '',
        portions: portions[recipeId] ?? null,
    }))
        .sort((a, b) => a.title.localeCompare(b.title, locale));
}
/**
 * The days a list was generated from, as a range the meal endpoint accepts.
 *
 * `meal_dates` is a set, not an interval: a list can cover Monday and Friday
 * without the days between. The bounds are what a query needs; the gaps are
 * harmless because a day with no meal simply yields none.
 */
export function shoppingListPeriod(mealDates) {
    if (mealDates.length === 0)
        return null;
    const sorted = [...mealDates].sort();
    return { from: sorted[0], to: sorted[sorted.length - 1] };
}
/** How many lists are kept readable offline at most, the latest included. */
export const SHOPPING_LISTS_IN_USE_MAX = 3;
/**
 * The lists a member may open next, latest first: the most recent one — the
 * courses screen's — and any other whose days are not all behind `today`,
 * which the home screen shows for the next planned dish.
 *
 * `lists` is in the API's order, most recent first. Lists without days only
 * count as the latest: with nothing to place them in time, older ones are
 * history, not shopping under way.
 */
export function shoppingListsInUse(lists, today, max = SHOPPING_LISTS_IN_USE_MAX) {
    return lists
        .filter((list, index) => {
        if (index === 0)
            return true;
        const period = shoppingListPeriod(list.meal_dates);
        return period !== null && period.to >= today;
    })
        .slice(0, max);
}
/** No-break space: a quantity never leaves its unit on the previous line. */
const NBSP = '\u00a0';
/**
 * A line's quantity as it is shown — « 1,5 kg », « 500 g », « 8 ».
 *
 * Pieces are written as a bare number: « 8 pièces » under « Œufs » spends a
 * word on what the name already says. Everything else keeps its unit, spelled
 * by the caller — the list used to print the raw column value, which is how a
 * French member ended up reading « 3 piece ».
 *
 * `other` is bare for a different reason: what it stood for is written in the
 * recipe's `unit_label`, a column a shopping line does not have. Spelled out
 * it gave « 1 autre », which names nothing at all.
 *
 * A unit without a number measures nothing, so it is not shown at all.
 */
export function formatShoppingQuantity(item, unitLabel) {
    const value = formatIngredientValue(item.quantity);
    if (!value)
        return null;
    const unit = item.unit?.trim();
    if (!unit || unit === DEFAULT_INGREDIENT_UNIT || unit === 'other')
        return value;
    return `${value}${NBSP}${unitLabel(unit)}`;
}
/**
 * The list as plain text, aisle by aisle, for sharing outside the app.
 *
 * The aisle and unit names are passed in rather than built here: the shared
 * text used to print the raw column values, so a French member sent their
 * family a list headed « PRODUCE » with « 3 piece » under it.
 */
export function formatShoppingListForSharing(items, labels, order) {
    const lines = [labels.title, ''];
    for (const group of groupItemsByAisle(items, order)) {
        lines.push(labels.aisle(group.category).toLocaleUpperCase());
        for (const item of group.items) {
            const quantity = formatShoppingQuantity(item, labels.unit);
            const suffix = quantity ? ` (${quantity})` : '';
            lines.push(`${item.checked ? '[x]' : '[ ]'} ${item.name}${suffix}`);
        }
        lines.push('');
    }
    return lines.join('\n').trimEnd();
}
/**
 * The list split the way a trolley splits it: what is still on the shelves,
 * and what is already in the basket.
 *
 * A ticked line leaves its aisle — the list shrinks as the shop is walked,
 * and what is left to find is the only thing in view. The basket keeps every
 * ticked line once, alphabetically, so one can be taken back out.
 */
export function splitBasket(items) {
    const toBuy = [];
    const inBasket = [];
    for (const item of items)
        (item.checked ? inBasket : toBuy).push(item);
    inBasket.sort((a, b) => a.name.localeCompare(b.name));
    return { toBuy, inBasket };
}
//# sourceMappingURL=shopping-grouping.js.map